import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function AboutSections() {
  const { t } = useTranslation();

  const researchCards = [
    { icon: "ri-file-list-3-line", title: t("research.spreadsheet.title"), text: t("research.spreadsheet.text") },
    { icon: "ri-message-2-line", title: t("research.review.title"), text: t("research.review.text") },
    { icon: "ri-truck-line", title: t("research.shipping.title"), text: t("research.shipping.text") },
    { icon: "ri-store-2-line", title: t("research.warehouse.title"), text: t("research.warehouse.text") },
    { icon: "ri-price-tag-3-line", title: t("research.coupon.title"), text: t("research.coupon.text") },
    { icon: "ri-shield-check-line", title: t("research.safety.title"), text: t("research.safety.text") },
  ];

  const steps = [
    { num: "01", icon: "ri-compass-3-line", title: t("beginner.step1.title"), text: t("beginner.step1.text") },
    { num: "02", icon: "ri-line-chart-line", title: t("beginner.step2.title"), text: t("beginner.step2.text") },
    { num: "03", icon: "ri-search-line", title: t("beginner.step3.title"), text: t("beginner.step3.text") },
  ];

  return (
    <>
      {/* What is */}
      <section id="what-is" className="bg-background-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("whatIs.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              {t("whatIs.text")}
            </p>
            <Link
              to="/guide/what-is"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t("whatIs.cta")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Research */}
      <section id="research" className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("research.title")}
            </h2>
            <p className="mt-2 text-base text-foreground-600">
              {t("research.subtitle")}
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {researchCards.map((card) => (
              <div
                key={card.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${card.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {card.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {card.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compare */}
      <section id="comparisons" className="bg-background-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("compare.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              {t("compare.text")}
            </p>
            <Link
              to="/guide/how-to-read"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary-500 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 whitespace-nowrap cursor-pointer"
            >
              {t("compare.cta")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Beginner steps */}
      <section id="beginner" className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("beginner.title")}
            </h2>
            <p className="mt-2 text-base text-foreground-600">
              {t("beginner.subtitle")}
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {steps.map((step) => (
              <div
                key={step.num}
                className="relative rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="font-heading text-4xl font-bold text-accent-500">
                  {step.num}
                </span>
                <span className="mt-3 w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className={`${step.icon} text-lg`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {step.text}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/guide/what-is"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t("beginner.cta")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}